import { useEffect, useState, type ReactNode } from 'react';
import { Shell } from '../Shell';
import { useAsync } from '../../core/useAsync';
import { getMe, getMessages, getRoster, postMessage } from '../../core/api';
import { clock, dayLabel } from '../../core/util';
import { avatarStyle, initials } from '../format';
import type { ChatMessage, Me, RosterMember } from '../../core/types';
import s from './Chat.module.css';

// League chat: one thread, everyone reads it. Wired to GET/POST /api/messages,
// with @-mention highlighting against the live roster.

function render(body: string, names: string[]): ReactNode {
  if (!names.length || body.indexOf('@') === -1) return body;
  const sorted = [...names].sort((a, b) => b.length - a.length);
  const out: ReactNode[] = [];
  let buf = '', i = 0, key = 0;
  const flush = () => { if (buf) { out.push(buf); buf = ''; } };
  while (i < body.length) {
    if (body[i] === '@' && (i === 0 || /\s/.test(body[i - 1]))) {
      const rest = body.slice(i + 1);
      const hit = sorted.find((n) => rest.toLowerCase().startsWith(n.toLowerCase()) && !/[a-z0-9]/i.test(rest[n.length] ?? ''));
      if (hit) { flush(); out.push(<span key={key++} className={s.mention}>@{rest.slice(0, hit.length)}</span>); i += 1 + hit.length; continue; }
    }
    buf += body[i++];
  }
  flush();
  return out;
}

const Send = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3 20v-6l8-2-8-2V4l19 8z" /></svg>;
const Info = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 7.5v.5" /></svg>;

export function Chat() {
  const roster = useAsync<RosterMember[]>(getRoster, []);
  const me = useAsync<Me>(getMe, []);
  const [msgs, setMsgs] = useState<ChatMessage[]>([]);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  const load = () => getMessages(undefined, 50).then((b) => setMsgs([...b].reverse())).catch(() => {});
  useEffect(() => { load(); }, []);

  const names = (roster.data ?? []).map((r) => r.display_name);
  const myId = me.data?.user?.user_id ?? null;

  async function send() {
    const v = body.trim();
    if (!v || sending) return;
    setSending(true);
    try {
      await postMessage(v);
      setBody('');
      await load();
    } catch { /* ignore */ } finally {
      setSending(false);
    }
  }

  let lastDay = '';
  return (
    <Shell>
      <div className={s.appbar}>
        <span className={s.ctx}>Chat<small>{roster.data ? `${roster.data.length} people · everyone reads this` : 'everyone reads this'}</small></span>
        <button className={s.menu} aria-label="Menu">⋯</button>
      </div>
      <div className={s.warn}><Info />Voting is open — keep the track talk cryptic.</div>
      <div className={s.log}>
        {msgs.length === 0 ? <div className={s.day}>No messages yet</div> : null}
        {msgs.map((m) => {
          const day = dayLabel(m.created_at);
          const showDay = day !== lastDay;
          lastDay = day;
          const self = m.user_id === myId;
          return (
            <div key={m.message_id}>
              {showDay ? <div className={s.day}>{day}</div> : null}
              <div className={`${s.msg}${self ? ' ' + s.self : ''}`}>
                {!self ? <span className={s.av} style={avatarStyle(m.avatar_url, m.user_id)}>{m.avatar_url ? '' : initials(m.display_name)}</span> : null}
                <div className={s.body}>
                  {!self ? <div className={s.meta}><b>{m.display_name}</b><span className={s.t}>{clock(m.created_at)}</span></div> : null}
                  <div className={s.bubble}>{render(m.body, names)}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className={s.compose}>
        <button className={s.emoji} aria-label="Emoji">😊</button>
        <input
          className={s.field}
          placeholder="Say something…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
        />
        <button className={s.send} aria-label="Send" onClick={send} disabled={!body.trim() || sending}><Send /></button>
      </div>
    </Shell>
  );
}
